import { Button, IconButton, Link, Stack, Tooltip, Typography } from '@mui/material'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { useState } from 'react'
import ReferenceDialog from './AddReferenceDialog'
import { Reference } from 'api/model'
import InfoOutlined from '@mui/icons-material/InfoOutlined'
import { COLORS } from 'app-constants'

// can delete or update an existing reference item
const ReferenceItem = ({
  reference,
  onDeleteReference,
  setSelectedReferenceIndex,
  index,
  setIsReferenceDialogOpen,
}: {
  reference: Reference
  onDeleteReference: (index: number) => void
  setSelectedReferenceIndex: React.Dispatch<React.SetStateAction<number>>
  index: number
  setIsReferenceDialogOpen: React.Dispatch<React.SetStateAction<boolean>>
}) => {
  const onDeleteReferenceClick = (index: number) => {
    onDeleteReference(index)
  }

  const onEditReferenceClick = () => {
    setSelectedReferenceIndex(index)
    setIsReferenceDialogOpen(true)
  }

  return (
    <Stack direction="row" alignItems="center" spacing={2} width="100%">
      <Tooltip title={reference.url} enterTouchDelay={0}>
        <Link
          variant="body0"
          href={`${reference.url}`}
          target="_blank"
          sx={{
            // on hover, show underline
            width: '100%',
            '&:hover': {
              textDecoration: 'underline',
            },
          }}>
          {reference.title}
        </Link>
      </Tooltip>
      <Stack direction="row" alignItems="center" spacing={2}>
        <IconButton
          onClick={onEditReferenceClick}
          sx={{
            width: '16px',
            height: '16px',
          }}>
          <EditOutlinedIcon />
        </IconButton>
        <IconButton
          onClick={() => {
            onDeleteReferenceClick(index)
          }}
          sx={{
            width: '16px',
            height: '16px',
          }}>
          <DeleteOutlinedIcon />
        </IconButton>
      </Stack>
    </Stack>
  )
}

const ReferencesList = ({
  references,
  setReferences,
  submitCount,
}: {
  references: Reference[]
  setReferences: React.Dispatch<React.SetStateAction<Reference[]>>
  submitCount: number
}) => {
  const [isReferenceDialogOpen, setIsReferenceDialogOpen] = useState(false)
  const onCloseDialog = () => {
    setIsReferenceDialogOpen(false)
  }
  const onAddReferenceClick = () => {
    setSelectedReferenceIndex(-1)
    setIsReferenceDialogOpen(true)
  }

  const onAddReference = (title: string, url: string) => {
    // add reference with title and url
    setReferences([...references, { title, url }])
  }

  const [selectedReferenceIndex, setSelectedReferenceIndex] = useState<number>(-1)

  const onDeleteReference = (index: number) => {
    // delete reference in the list that matches the index
    setReferences(references.filter((_, idx) => idx !== index))
  }

  const onUpdateReference = (index: number, title: string, url: string) => {
    const updatedReference = { title, url }
    setReferences(references.map((reference, idx) => (idx === index ? updatedReference : reference)))
  }

  return (
    <Stack mt={2} width="100%">
      <Stack direction="row" alignItems="center" spacing={1} mb={1}>
        <Typography
          variant="h6"
          sx={{
            color: COLORS.GRAY_0,
          }}>
          References
        </Typography>

        <Tooltip
          title="Add references to provide external sites that may be helpful to the other user."
          enterTouchDelay={0}>
          <InfoOutlined
            sx={{
              width: '16px',
              height: '16px',
              fill: COLORS.GRAY_3,
            }}
          />
        </Tooltip>
      </Stack>

      <Stack width="100%" spacing="4px">
        {references.map((reference, index) => (
          <ReferenceItem
            key={index}
            index={index}
            reference={reference}
            onDeleteReference={onDeleteReference}
            setSelectedReferenceIndex={setSelectedReferenceIndex}
            setIsReferenceDialogOpen={setIsReferenceDialogOpen}
          />
        ))}
      </Stack>
      <Button
        onClick={onAddReferenceClick}
        sx={{
          mt: 1,
          width: 'fit-content',
        }}>
        Add reference
      </Button>

      {isReferenceDialogOpen && (
        <ReferenceDialog
          references={references}
          onAddReference={onAddReference}
          onDeleteReference={onDeleteReference}
          onUpdateReference={onUpdateReference}
          submitCount={submitCount}
          referenceIndex={selectedReferenceIndex}
          onCloseDialog={onCloseDialog}
        />
      )}
    </Stack>
  )
}

export default ReferencesList
